import * as Yup from 'yup';
import moment, { Moment } from 'moment';
import { dateToLocal, dateToUtc } from '@/common/utils/dates';
import { getDateObject, isValidDateFormat } from '@/library/utils/dates';
import { strToNumber } from '@/library/utils/numbers';
import { format, formatInTimeZone } from 'date-fns-tz';
import { DateInputType } from '@webthatmatters/orca-dateinput';
import { startOfYear } from 'date-fns';

type FieldFuncParams = {
  required?: boolean;
  isRequired?: boolean;
  initialValue?: any;
};

type FieldFuncType = (params?: FieldFuncParams) => any;

export type FormConfigFieldType =
  | FieldFuncType
  | FieldConfigType
  | Record<
      string,
      FieldFuncType | FieldConfigType | Record<string, FieldFuncType | FieldConfigType>
    >;

export type FormConfigType = Record<string, FormConfigFieldType | undefined>;

type FieldConfigType = {
  required?: boolean;
  isRequired?: boolean;
  validation?: Yup.AnySchema;
  decimalScale?: number;
  transformOnLoad?: (value: any) => any;
  transformOnSubmit?: (value: any) => any;
  initialValue?: any;
};

type SingleDateFieldConfigType = FieldConfigType & {
  transformOnLoad: (d: string | null) => Date | null;
  transformOnSubmit: (d: Date | null) => string | null;
};

type FieldTypeKey =
  | 'number'
  | 'date'
  | 'dateTime'
  | 'utcDateTimeField'
  | 'singleDate'
  | 'singleDateTime'
  | 'singleYear'
  | 'string'
  | 'option'
  | 'boolean'
  | 'hidden';

const types: Record<FieldTypeKey, FieldConfigType> = {
  number: {
    initialValue: null,
    transformOnLoad: (value: string | null) => {
      if (value === null) return '';
      return parseFloat(value).toLocaleString('en', { maximumFractionDigits: 2 });
    },
    transformOnSubmit: (value: any): number | null => {
      if (isNaN(value)) {
        if (!value || value.length === 0) return null;
        return strToNumber(value);
      } else if (value === '') {
        return null;
      } else {
        return value;
      }
    },
    validation: Yup.number()
      .nullable() // Allow NULL values
      .strict(false) // Dont throw errors for Numeric strings
      .transform((cv, ov) => (ov === '' ? undefined : cv)) // Dont throw errors when value is ''
      .typeError('This must be a numeric value')
  },
  date: {
    // Deprecated, use singleDate instead
    initialValue: null,
    transformOnLoad: (m: string | null) => (m ? moment(m) : null),
    transformOnSubmit: (m: Moment | null) => (m ? m.format('YYYY-MM-DD') : null)
  },
  dateTime: {
    // Deprecated, use singleDateTime instead
    initialValue: null,
    transformOnLoad: (m: string | null) => (m ? dateToLocal(m) : null),
    transformOnSubmit: (m: Moment | null) => {
      if (!m) return null;
      const utcDate = dateToUtc(m.format('YYYY-MM-DD HH:mm:ss')) as Moment | null;
      return utcDate ? utcDate.format('YYYY-MM-DD HH:mm:ss') : null;
    }
  },
  utcDateTimeField: {
    initialValue: null,
    transformOnLoad: (m: string | null) => (m ? moment(m) : null),
    transformOnSubmit: (m: Moment | null) => (m ? m.format('YYYY-MM-DD HH:mm:ss') : null)
  },
  singleDate: {
    initialValue: null,
    transformOnLoad: (d: string | null) =>
      d ? (isValidDateFormat(d) ? getDateObject(d) : format(d, 'yyyy-MM-dd')) : null,
    transformOnSubmit: (d: DateInputType) => (d ? format(d, 'yyyy-MM-dd') : null)
  },
  singleDateTime: {
    initialValue: null,
    transformOnLoad: (d: string | null) => getDateObject(d),
    transformOnSubmit: (d: DateInputType) =>
      d ? formatInTimeZone(d, 'UTC', 'yyyy-MM-dd HH:mm') : null
  },
  singleYear: {
    initialValue: null,
    transformOnLoad: (year: number | null) => (year ? startOfYear(new Date(year, 0, 1)) : null),
    transformOnSubmit: (d: DateInputType) => (d ? +format(d, 'yyyy') : null)
  },
  string: {
    initialValue: null
  },
  option: {
    initialValue: null
  },
  boolean: {
    initialValue: true,
    validation: Yup.boolean()
  },
  hidden: {
    initialValue: null
  }
};

const createFieldType = <K extends FieldTypeKey>(
  type: K,
  fieldConfig: Partial<FieldConfigType> = {}
): FieldConfigType => {
  const { email, required, positive, decimalScale, ...overrides } = fieldConfig as any;

  let config: FieldConfigType = { ...types[type], ...overrides };

  if (required) {
    config = {
      ...config,
      isRequired: true
    };
    config = addToValidation(config, v => v.required());
  }

  if (email) {
    config = {
      ...config,
      validation: Yup.string()
    };
    config = addToValidation(config, v => (v as Yup.StringSchema).email());
  }

  if (positive && type === 'number') {
    config = addToValidation(config, v => (v as Yup.NumberSchema).positive());
  }

  if ((decimalScale || decimalScale === 0) && !overrides.transformOnLoad) {
    config = {
      ...config,
      transformOnLoad: (value: string | null) => {
        if (value === null) return '';
        return parseFloat(value).toLocaleString('en', { maximumFractionDigits: decimalScale });
      }
    };
  }

  return config;
};

const addToValidation = (
  config: FieldConfigType,
  cb: (v: Yup.AnySchema) => Yup.AnySchema
): FieldConfigType => {
  const validation = config.validation || Yup.mixed();
  return { ...config, validation: cb(validation) };
};

// Wrappers
export const dateField = (config: Partial<FieldConfigType> = {}) => createFieldType('date', config);
export const dateTimeField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('dateTime', config);
export const utcDateTimeField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('utcDateTimeField', config);

export const singleDateField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('singleDate', config) as SingleDateFieldConfigType;
export const singleDateTimeField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('singleDateTime', config) as SingleDateFieldConfigType;
export const singleYearField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('singleYear', config) as SingleDateFieldConfigType;

export const numberField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('number', config);
export const rateField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('number', {
    ...config,
    transformOnLoad: (value: string | null) => {
      // Rate precision is 8 decimals. This is always formatted by API.
      if (value === null) return '';
      return value;
    }
  });
export const stringField = (config: Partial<FieldConfigType & { email?: boolean }> = {}) =>
  createFieldType('string', config);
export const optionField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('option', config);
export const booleanField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('boolean', config);
export const hiddenField = (config: Partial<FieldConfigType> = {}) =>
  createFieldType('hidden', config);

export const arrayOf = (config: Partial<FieldConfigType> = {}) => ({
  initialValue: [] as any[],
  transformOnLoad: config.transformOnLoad
    ? (values: any[]) => values.map(config.transformOnLoad!)
    : undefined,
  transformOnSubmit: config.transformOnSubmit
    ? (values: any[]) => values.map(config.transformOnSubmit!)
    : undefined,
  validation: config.validation ? Yup.array().of(config.validation) : Yup.array()
});
